import React from "react";

const BASE_URL = "https://api.portpilot.co/api/v1";

const SectionTitle = ({ children }) => (
  <h2 className="mt-10 text-xl font-semibold text-gray-900">{children}</h2>
);

const CodeBlock = ({ children }) => (
  <pre className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4 text-sm text-gray-100">
    <code>{children}</code>
  </pre>
);

export default function Developers() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6">
      <h1 className="text-3xl font-bold tracking-tight text-gray-900">PortPilot Developer Documentation</h1>
      <p className="mt-3 text-gray-700">
        External API v1 provides read-only logistics data access with tenant isolation and API key authentication.
      </p>

      <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">Base URL</p>
        <p className="mt-1 break-all font-mono text-sm text-blue-900">{BASE_URL}</p>
      </div>

      <SectionTitle>Authentication</SectionTitle>
      <p className="mt-3 text-gray-700">
        Use an API key in the <span className="font-mono">Authorization</span> header with Bearer format.
      </p>
      <CodeBlock>{`Authorization: Bearer <your_api_key>`}</CodeBlock>
      <p className="mt-3 text-gray-700">
        Keys are scoped to a single tenant (<span className="font-mono">cust_code</span>). Requests outside that
        tenant return <span className="font-mono">404 NOT_FOUND</span>.
      </p>

      <SectionTitle>Error Handling</SectionTitle>
      <p className="mt-3 text-gray-700">All errors use a consistent envelope:</p>
      <CodeBlock>{`{
  "error": {
    "code": "UNAUTHORIZED",
    "message": "Missing or invalid bearer token."
  },
  "meta": {
    "version": "v1",
    "request_id": "..."
  }
}`}</CodeBlock>
      <p className="mt-3 text-gray-700">Common error codes:</p>
      <ul className="mt-2 list-disc space-y-1 pl-6 text-gray-700">
        <li>
          <span className="font-mono">UNAUTHORIZED</span> (401): missing, invalid, revoked, or expired key
        </li>
        <li>
          <span className="font-mono">IP_NOT_ALLOWED</span> (403): source IP not in allowlist
        </li>
        <li>
          <span className="font-mono">NOT_FOUND</span> (404): resource not found or outside tenant scope
        </li>
        <li>
          <span className="font-mono">METHOD_NOT_ALLOWED</span> (405): only GET is allowed in v1
        </li>
        <li>
          <span className="font-mono">RATE_LIMITED</span> (429): per-minute key rate limit exceeded
        </li>
        <li>
          <span className="font-mono">DAILY_QUOTA_EXCEEDED</span> (429): daily key quota exceeded
        </li>
        <li>
          <span className="font-mono">VALIDATION_ERROR</span> (400): query parameter validation failed
        </li>
      </ul>

      <SectionTitle>Rate Limits and Quota</SectionTitle>
      <ul className="mt-3 list-disc space-y-1 pl-6 text-gray-700">
        <li>Per-key per-minute rate limit is enforced server-side.</li>
        <li>Per-key daily quota can be configured by your PortPilot administrator.</li>
        <li>
          429 responses may include a <span className="font-mono">Retry-After</span> header.
        </li>
      </ul>

      <SectionTitle>Response Envelope</SectionTitle>
      <p className="mt-3 text-gray-700">Successful responses use:</p>
      <CodeBlock>{`{
  "data": ...,
  "meta": {
    "version": "v1",
    "request_id": "...",
    "tenant": { "cust_code": "WLGBL" },
    "pagination": { "limit": 50, "offset": 0, "count": 50, "total": 123 }
  }
}`}</CodeBlock>

      <SectionTitle>Endpoint Reference</SectionTitle>
      <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 font-semibold text-gray-700">Method</th>
              <th className="px-4 py-3 font-semibold text-gray-700">Path</th>
              <th className="px-4 py-3 font-semibold text-gray-700">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
            <tr>
              <td className="px-4 py-3 font-mono">GET</td>
              <td className="px-4 py-3 font-mono">/health</td>
              <td className="px-4 py-3">Service status check</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">GET</td>
              <td className="px-4 py-3 font-mono">/pos</td>
              <td className="px-4 py-3">Summary PO list by date range (partner sync)</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">GET</td>
              <td className="px-4 py-3 font-mono">/pos/{`{po_number}`}</td>
              <td className="px-4 py-3">PO detail by PO number</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">GET</td>
              <td className="px-4 py-3 font-mono">/routing</td>
              <td className="px-4 py-3">Summary PO + Load list by date range (partner sync)</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">GET</td>
              <td className="px-4 py-3 font-mono">/routing/loads</td>
              <td className="px-4 py-3">Filtered routing load list (legacy list endpoint)</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">GET</td>
              <td className="px-4 py-3 font-mono">/routing/loads/{`{load_id}`}</td>
              <td className="px-4 py-3">Routing load detail by load ID</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono">GET</td>
              <td className="px-4 py-3 font-mono">/routing/pos/{`{po_number}`}</td>
              <td className="px-4 py-3">Get routing POS rows for one PO</td>
            </tr>
          </tbody>
        </table>
      </div>

      <SectionTitle>POS API Rules</SectionTitle>
      <p className="mt-3 text-gray-700">
        <span className="font-mono">GET /pos</span> is a summary list endpoint. It returns only PO identifiers for
        sync workflows.
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-6 text-gray-700">
        <li>Allowed params: po_number, mabd_from, mabd_to, create_date_from, create_date_to, limit, offset</li>
        <li>Required: at least one date range input from mabd_* or create_date_*</li>
        <li>Default limit 50, max limit 500, offset must be greater than or equal to 0</li>
        <li>Response data shape: array of objects with po_number only</li>
      </ul>
      <CodeBlock>{`GET /pos success example (summary)
{
  "data": [
    { "po_number": "1081948720" },
    { "po_number": "1133512708" }
  ],
  "meta": {
    "version": "v1",
    "request_id": "...",
    "tenant": { "cust_code": "WLGBL" },
    "pagination": { "count": 2, "limit": 2, "offset": 0, "total": 347 }
  }
}`}</CodeBlock>
      <p className="mt-4 text-gray-700">
        <span className="font-mono">GET /pos/{`{po_number}`}</span> returns detail payload and includes top-level
        <span className="font-mono"> create_date</span>.
      </p>

      <SectionTitle>Routing API Rules</SectionTitle>
      <p className="mt-3 text-gray-700">
        <span className="font-mono">GET /routing</span> is the routing summary endpoint for partner sync.
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-6 text-gray-700">
        <li>
          Allowed params: confirmed_date_from, confirmed_date_to, shipped_date_from, shipped_date_to, limit, offset
        </li>
        <li>Required: at least one date range input from confirmed_date_* or shipped_date_*</li>
        <li>Default limit 50, max limit 500, offset must be greater than or equal to 0</li>
        <li>Response data shape: array of objects with po_number and load_id only</li>
        <li>Old params created_at_from and created_at_to are rejected as unknown_query_params</li>
      </ul>
      <CodeBlock>{`GET /routing success example (summary)
{
  "data": [
    { "po_number": "1081948720", "load_id": "91958351" },
    { "po_number": "1133512708", "load_id": "91976508" }
  ],
  "meta": {
    "version": "v1",
    "request_id": "...",
    "tenant": { "cust_code": "WLGBL" },
    "pagination": { "count": 2, "limit": 2, "offset": 0, "total": 337 }
  }
}`}</CodeBlock>
      <p className="mt-4 text-gray-700">
        <span className="font-mono">GET /routing/loads/{`{load_id}`}</span> returns detail; load rows include
        <span className="font-mono"> confirmed_date</span>.
      </p>
      <p className="mt-2 text-gray-700">
        <span className="font-mono">GET /routing/pos/{`{po_number}`}</span> returns routing-by-PO rows and includes
        <span className="font-mono"> mabd</span>, <span className="font-mono">confirmed_date</span>, and
        <span className="font-mono"> shipped_date</span> per row.
      </p>

      <SectionTitle>Examples</SectionTitle>
      <p className="mt-3 text-gray-700">curl</p>
      <CodeBlock>{`curl -sS "${BASE_URL}/pos?mabd_from=2025-11-10&mabd_to=2026-03-07&limit=50&offset=0" \\
  -H "Authorization: Bearer <your_api_key>"`}</CodeBlock>

      <p className="mt-6 text-gray-700">Python</p>
      <CodeBlock>{`import requests

BASE_URL = "${BASE_URL}"
API_KEY = "<your_api_key>"

summary_resp = requests.get(
    f"{BASE_URL}/routing",
    params={
        "confirmed_date_from": "2025-10-29",
        "confirmed_date_to": "2025-10-29",
        "limit": 50,
        "offset": 0,
    },
    headers={"Authorization": f"Bearer {API_KEY}"},
    timeout=30,
)
summary_resp.raise_for_status()
summary_data = summary_resp.json()
print(summary_data)

first_load_id = summary_data["data"][0]["load_id"]
detail_resp = requests.get(
    f"{BASE_URL}/routing/loads/{first_load_id}",
    headers={"Authorization": f"Bearer {API_KEY}"},
    timeout=30,
)
detail_resp.raise_for_status()
print(detail_resp.json())
`}</CodeBlock>

      <p className="mt-6 text-gray-700">Node.js</p>
      <CodeBlock>{`const BASE_URL = "${BASE_URL}";
const API_KEY = "<your_api_key>";

const url = new URL(BASE_URL + "/routing");
url.searchParams.set("shipped_date_from", "2025-11-06");
url.searchParams.set("shipped_date_to", "2026-02-09");
url.searchParams.set("limit", "50");
url.searchParams.set("offset", "0");

const res = await fetch(url, {
  method: "GET",
  headers: {
    Authorization: "Bearer " + API_KEY,
  },
});

if (!res.ok) {
  throw new Error("Request failed: " + res.status);
}

const summaryBody = await res.json();
console.log(summaryBody);

const poNumber = summaryBody.data[0]?.po_number;
if (poNumber) {
  const poRes = await fetch(BASE_URL + "/routing/pos/" + poNumber, {
    method: "GET",
    headers: { Authorization: "Bearer " + API_KEY },
  });
  if (!poRes.ok) {
    throw new Error("Routing by PO failed: " + poRes.status);
  }
  const poBody = await poRes.json();
  console.log(poBody);
}`}</CodeBlock>
    </div>
  );
}
